import * as Cesium from 'cesium';
import { APP_CONFIG, QUALITY_SETTINGS } from '../config/appConfig';
import { DATA_SOURCES } from '../config/dataSources';
import { buildingTilesetOptions } from '../cesium/performance';

export interface BuildingLod2Coverage {
  rectangle: Cesium.Rectangle;
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface BuildingLod2LoadResult {
  tileset: Cesium.Cesium3DTileset;
  coverage: BuildingLod2Coverage;
  mode: 'clipping' | 'visibility-toggle';
  setVisible: (visible: boolean) => void;
}

function coverageFromTileset(
  tileset: Cesium.Cesium3DTileset,
): BuildingLod2Coverage {
  const rectangle = Cesium.Rectangle.fromBoundingSphere(tileset.boundingSphere);
  return {
    rectangle,
    west: Cesium.Math.toDegrees(rectangle.west),
    south: Cesium.Math.toDegrees(rectangle.south),
    east: Cesium.Math.toDegrees(rectangle.east),
    north: Cesium.Math.toDegrees(rectangle.north),
  };
}

function coveragePolygon(coverage: BuildingLod2Coverage): Cesium.ClippingPolygon {
  const { west, south, east, north } = coverage;
  return new Cesium.ClippingPolygon({
    positions: Cesium.Cartesian3.fromDegreesArray([
      west,
      south,
      east,
      south,
      east,
      north,
      west,
      north,
    ]),
  });
}

export async function loadBuildingLod2(
  viewer: Cesium.Viewer,
  lod1: Cesium.Cesium3DTileset | null,
  onError: (error: unknown) => void,
): Promise<BuildingLod2LoadResult> {
  try {
    const tileset = await Cesium.Cesium3DTileset.fromUrl(
      DATA_SOURCES.buildingLod2,
      {
        ...buildingTilesetOptions(),
        maximumScreenSpaceError:
          QUALITY_SETTINGS[APP_CONFIG.defaultQuality]
            .buildingLod2MaximumScreenSpaceError,
        cacheBytes: QUALITY_SETTINGS[APP_CONFIG.defaultQuality]
          .buildingLod2CacheBytes,
        maximumCacheOverflowBytes: 64 * 1024 * 1024,
        scene: viewer.scene,
        // LOD2の読込完了後に、置換方式を決めて表示する。
        show: false,
      },
    );
    viewer.scene.primitives.add(tileset);
    tileset.shadows = QUALITY_SETTINGS[APP_CONFIG.defaultQuality].shadows
      ? Cesium.ShadowMode.ENABLED
      : Cesium.ShadowMode.DISABLED;

    const coverage = coverageFromTileset(tileset);
    const clippingSupported = Cesium.ClippingPolygonCollection.isSupported(
      viewer.scene,
    );

    if (lod1 && clippingSupported) {
      const clippingPolygons = new Cesium.ClippingPolygonCollection({
        polygons: [coveragePolygon(coverage)],
        // LOD1をLOD2実範囲の内側だけ非表示にする。
        inverse: false,
      });
      lod1.clippingPolygons = clippingPolygons;

      const setVisible = (visible: boolean): void => {
        tileset.show = visible;
        clippingPolygons.enabled = visible;
        viewer.scene.requestRender();
      };
      setVisible(true);
      return { tileset, coverage, mode: 'clipping', setVisible };
    }

    // WebGL2/ClippingPolygon非対応時は、LOD1とLOD2を重ねない切替へフォールバックする。
    const setVisible = (visible: boolean): void => {
      tileset.show = visible;
      if (lod1) lod1.show = !visible;
      viewer.scene.requestRender();
    };
    setVisible(true);
    return { tileset, coverage, mode: 'visibility-toggle', setVisible };
  } catch (error) {
    onError(error);
    throw error;
  }
}
